import { Plus, X } from "lucide-react";
import {
  DimensionUnitSchema,
  FP_CUSTOM_SOURCE_PREFIX,
  WeightUnitSchema,
  type PublicQuoteLine,
} from "@forgepoint/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { lineFieldLabel } from "./lineFields";
import { isItemLine, readSampleLineValue } from "./sampleQuote";
import { useFeatureFlag } from "@/store/featureFlagStore";
import { getItemUnitOptions } from "@/modules/items/itemUnitOptions";

/** Line sources the sample rows cannot edit, per the design's scope. */
const READ_ONLY_LINE_SOURCES: readonly string[] = [
  "number",
  "item-image",
  "dimension-unit-symbol",
  "weight-unit-symbol",
];

/** Line sources rendered as a fixed-option picker rather than free text. */
const LINE_SOURCE_OPTIONS: Record<string, readonly string[] | undefined> = {
  "weight-unit": WeightUnitSchema.options,
  "dimension-unit": DimensionUnitSchema.options,
};

interface SampleLinesEditorProps {
  /** The line-scope sources the template binds, in document order. */
  lineSources: readonly string[];
  /** The template's sample line rows. */
  lines: readonly PublicQuoteLine[];
  /** Writes one line-scope value into a row. */
  onLineChange: (lineId: string, source: string, value: string) => void;
  /** Appends a fresh sample item row. */
  onAddLine: () => void;
  /** Removes a sample item row. */
  onRemoveLine: (lineId: string) => void;
}

/**
 * The line-scope half of the sample form: one control per bound line source on
 * each sample row. Section and note rows expose only their description, since
 * they exist to exercise the `sections` and `notes` repeater filters.
 * @param lineSources - The line-scope sources the template binds
 * @param lines - The template's sample line rows
 * @param onLineChange - Writes one line-scope value into a row
 * @param onAddLine - Appends a fresh sample item row
 * @param onRemoveLine - Removes a sample item row
 * @returns The sample line rows editor
 */
export function SampleLinesEditor({
  lineSources,
  lines,
  onLineChange,
  onAddLine,
  onRemoveLine,
}: SampleLinesEditorProps) {
  const editableSources = lineSources.filter(
    (source) => !READ_ONLY_LINE_SOURCES.includes(source),
  );

  if (editableSources.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        Sample lines
      </h3>
      {lines.map((line, index) => (
        <div key={line.id} className="space-y-2 rounded-md border p-3">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {rowTitle(line, index)}
            </span>
            {isItemLine(line) && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Remove sample row"
                onClick={() => onRemoveLine(line.id)}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
          {rowSources(line, editableSources).map((source) => (
            <SampleLineInput
              key={source}
              lineId={line.id}
              source={source}
              value={readSampleLineValue(line, source)}
              onChange={onLineChange}
            />
          ))}
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={onAddLine}>
        <Plus className="size-4" /> Add item row
      </Button>
    </section>
  );
}

interface SampleLineInputProps {
  /** The row being edited. */
  lineId: string;
  /** The line-scope source this control writes. */
  source: string;
  /** The current value. */
  value: string;
  /** Writes the value back to the row. */
  onChange: (lineId: string, source: string, value: string) => void;
}

/**
 * One sample line control: a picker for the enum-backed unit sources, plain
 * text otherwise.
 * @param lineId - The row being edited
 * @param source - The line-scope source this control writes
 * @param value - The current value
 * @param onChange - Writes the value back to the row
 * @returns The labelled control
 */
function SampleLineInput({
  lineId,
  source,
  value,
  onChange,
}: SampleLineInputProps) {
  const epicorPrimitiveSupportEnabled = useFeatureFlag(
    "epicor-primitive-support",
  );
  const id = `sample-line-${lineId}-${source}`;
  const options =
    source === "unit"
      ? getItemUnitOptions(epicorPrimitiveSupportEnabled, value)
      : LINE_SOURCE_OPTIONS[source];
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{sourceLabel(source)}</Label>
      {options ? (
        <Select
          value={value}
          onValueChange={(next) => onChange(lineId, source, next)}
        >
          <SelectTrigger id={id}>
            <SelectValue placeholder="Select" />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option} value={option}>
                {option}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <Input
          id={id}
          value={value}
          onChange={(event) => onChange(lineId, source, event.target.value)}
        />
      )}
    </div>
  );
}

/**
 * The heading of one sample row, so the author can tell the rows apart.
 * @param line - The sample line row
 * @param index - The row's position in the sample lines
 * @returns The row heading
 */
function rowTitle(line: PublicQuoteLine, index: number): string {
  if (line.type === "ITEM") {
    return `Item ${index + 1}`;
  }
  return line.type === "SECTION" ? "Section" : "Note";
}

/**
 * The sources a row can edit: everything bound for item rows, the description
 * alone for the fixed section and note rows.
 * @param line - The sample line row
 * @param editableSources - The bound, editable line sources
 * @returns The sources to render controls for
 */
function rowSources(
  line: PublicQuoteLine,
  editableSources: readonly string[],
): string[] {
  if (line.type === "ITEM") {
    return [...editableSources];
  }
  return editableSources.filter((source) => source === "description");
}

/**
 * The friendly label of a line source, naming custom fields by their raw name.
 * @param source - The line-scope source key
 * @returns The label shown beside the control
 */
function sourceLabel(source: string): string {
  return source.startsWith(FP_CUSTOM_SOURCE_PREFIX)
    ? source.slice(FP_CUSTOM_SOURCE_PREFIX.length)
    : lineFieldLabel(source);
}
